import type { ObjectMetadata } from '@olytic/foundation';

import { leadName, OBJECT_DEFS } from '../../objects/objectDefs';
import { activityColumns, asDate, asDateTime, asLabel, column, createdByField, lastModifiedByField, listColumnsOf, related, type Row, schemaRecord, text, urlField } from './kit';

const def = OBJECT_DEFS.leads;

/**
 * Its fields come from the database (the server's SCHEMA_OBJECTS), shown by
 * their type, the Opportunity linked to its record; any column not placed
 * below appears in Other Fields (MOTHER-297).
 */
const field = schemaRecord('leads');

const details = [
  field('first_name', 'First Name'),
  field('last_name', 'Last Name'),
  field('email', 'Email'),
  field('phone', 'Phone'),
  field('log_texts_calls', 'Log Texts & Calls'),
  field('company_name', 'Company'),
  field('status', 'Status'),
  field('opportunity_name', 'Opportunity'),
  field('maturity_tier', 'Maturity'),
  // A score as written, not a label.
  field('urgency_score', 'Urgency', { format: text }),
  field('leverage_point', 'Leverage Point'),
  urlField('pdf_url', 'Roadmap PDF'),
  field('q2_team_size', 'Team Size'),
  field('q3_revenue_range', 'Revenue Range'),
  field('q4_revenue_model', 'Revenue Model'),
  field('q6_ai_maturity', 'AI Maturity'),
  field('q7_team_readiness', 'Team Readiness'),
  field('q9_timeline', 'Timeline'),
  field('q7b_owned_phases', 'Phases They Own', { wide: true }),
  field('q5_primary_pain', 'Primary Pain', { wide: true }),
  field('q8_stated_goal', 'Stated Goal', { wide: true }),
  field('q10_one_fix', 'The One Fix', { wide: true }),
  field('source', 'Source'),
  field('created_at', 'Submitted'),
  createdByField(),
  lastModifiedByField(),
];

/** A lead: someone who took the AI roadmap assessment or left an email with Ollie. Rationale: leads.md. */
export const leads: ObjectMetadata<Row> = {
  key: def.key,
  singular: 'Lead',
  plural: def.label,
  icon: def.icon,
  listColumns: listColumnsOf(def, details),
  details,
  sections: [
    { key: 'identity', fields: ['first_name', 'last_name', 'email', 'phone', 'log_texts_calls', 'company_name', 'status', 'opportunity_name', 'maturity_tier', 'urgency_score', 'leverage_point', 'pdf_url'] },
    {
      key: 'answers',
      label: 'Assessment Answers',
      fields: ['q2_team_size', 'q3_revenue_range', 'q4_revenue_model', 'q6_ai_maturity', 'q7_team_readiness', 'q9_timeline', 'q7b_owned_phases', 'q5_primary_pain', 'q8_stated_goal', 'q10_one_fix'],
    },
    { key: 'system', label: 'System', collapsed: true, fields: ['source', 'created_at', 'created_by_name', 'last_modified_by_name'] },
  ],
  related: [
    // Their chats with Ollie on olytic.com (migration 0062), newest first.
    related('agent-conversations', 'Agent Conversations', 'agent-conversations', [
      column('name', 'Conversation', { primary: true }),
      column('opening_message', 'Opening Message'),
      column('outcome', 'Outcome', { format: asLabel }),
      column('created_at', 'Started', { format: asDateTime }),
    ]),
    // Notes and other activities linked to the lead (0067, MOTHER-56), newest first.
    related('activities', 'Activities', 'activities', [
      ...activityColumns().slice(0, 2),
      column('occurred_at', 'Occurred at', { format: asDate }),
    ]),
  ],
  tabs: [
    { key: 'information', label: 'Information', details: true },
    // The Activity tab (the lead's touches as a timeline, MOTHER-239) is the one ObjectRecordView adds after
    // Information, so no tab here is keyed `activity`.
    { key: 'conversations', label: 'Conversations', related: ['agent-conversations', 'activities'] },
  ],
  titleOf: (row) => text(leadName(row)),
  searchKeys: ['first_name', 'last_name', 'email', 'company_name', 'phone'],
};
